// Kontroly souladu na pozadí (krok 12.3, 05/1610; kontrakt tag Fronty, 08 §3.28a):
//   GET    /kontroly-souladu/pravidla               katalog pravidel: popis, adresáti, přepínač, počty nálezů a výjimek
//   GET    /kontroly-souladu/nalezy                 nálezy (signály druhu `kontrola_souladu`) podle pravidla, závažnosti
//                                                    a oblasti, s počty podle závažnosti (souhrn)
//   GET    /kontroly-souladu/vyjimky                výjimky „Ignorovat s důvodem“ (platné, nebo odebrané)
//   PUT    /kontroly-souladu/pravidla/{kod}         zapnout / vypnout pravidlo (admin, jednatel)
//   POST   /kontroly-souladu/prepocitat             přepočítat teď (admin, jednatel)
//   POST   /signaly/{id}/ignorovat                  „Ignorovat s důvodem“ u nálezu — uloží výjimku
//   DELETE /kontroly-souladu/vyjimky/{id}           „Odebrat výjimku“ (nález se zase ohlásí)
//   POST   /kontroly-souladu/vyjimky/{id}/obnovit   Zpět po odebrání výjimky
// O tom, jestli nesoulad je, rozhoduje server (pravidla v kódu); obrazovka jen ukazuje a posílá povely.
import type { Schema } from '@ecoser/shared';
import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './klient';
import type { SignalSeznam } from './prehled';

export type PravidloSouladu = Schema<'KontrolaSouladuPravidlo'>;
export type PrehledKontrol = Schema<'KontrolySouladuPrehled'>;
export type VyjimkaSouladu = Schema<'KontrolaSouladuVyjimka'>;
export type VyjimkySouladuSeznam = Schema<'KontrolaSouladuVyjimkaSeznam'>;
export type IgnorovanoSouladu = Schema<'KontrolaSouladuIgnorovano'>;
export type PrepocetKontrol = Schema<'KontrolySouladuPrepocet'>;

/** Katalog pravidel, nálezy a výjimky smí číst admin, jednatel, dispečer, koordinátor a vedoucí obchodu. */
export const AKCE_CIST_KONTROLY = 'kontroly_souladu.cist';
/** Zapnout a vypnout pravidlo, přepočítat teď — admin a jednatel. */
export const AKCE_UPRAVIT_KONTROLY = 'kontroly_souladu.upravit';
/** Ignorovat nález s důvodem a vzít výjimku zpět. */
export const AKCE_IGNOROVAT_KONTROLY = 'kontroly_souladu.ignorovat';

export const KLIC_KONTROLY = ['kontroly-souladu'] as const;
export const VELIKOST_VYJIMEK = 25;

export function useKatalogKontrol(volby: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KLIC_KONTROLY, 'pravidla'] as const,
    queryFn: ({ signal }) => api.get<PrehledKontrol>('/kontroly-souladu/pravidla', { signal }),
    enabled: volby.enabled ?? true,
  });
}

export interface FiltrVyjimek {
  pravidlo?: string;
  hledat?: string;
  smazane?: boolean;
  strana?: number;
}

function dotazVyjimek(filtr: FiltrVyjimek): string {
  const parametry = new URLSearchParams({
    strana: String(filtr.strana ?? 1),
    velikost: String(VELIKOST_VYJIMEK),
  });
  if (filtr.pravidlo) parametry.set('pravidlo', filtr.pravidlo);
  if (filtr.hledat?.trim()) parametry.set('hledat', filtr.hledat.trim());
  if (filtr.smazane) parametry.set('smazane', 'true');
  return parametry.toString();
}

export function useVyjimkyKontrol(filtr: FiltrVyjimek, volby: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KLIC_KONTROLY, 'vyjimky', filtr] as const,
    queryFn: ({ signal }) =>
      api.get<VyjimkySouladuSeznam>(`/kontroly-souladu/vyjimky?${dotazVyjimek(filtr)}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: volby.enabled ?? true,
  });
}

/** Nálezy jednoho pravidla (otevřené, nejzávažnější první) — jen ty, které přihlášený smí vidět. */
export function useNalezyKontrol(pravidlo: string, volby: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...KLIC_KONTROLY, 'nalezy', pravidlo] as const,
    queryFn: ({ signal }) =>
      api.get<SignalSeznam>(
        `/kontroly-souladu/nalezy?pravidlo=${encodeURIComponent(pravidlo)}&velikost=20`,
        { signal },
      ),
    enabled: volby.enabled ?? true,
  });
}

/** Filtr souhrnu nálezů (kontrola faktura × smlouva × evidence, 10. 10. 2026): závažnost a oblast pravidel. */
export interface FiltrNalezu {
  zavaznost?: 'info' | 'upozorneni' | 'kriticke';
  oblast?: string;
}

/** Otevřené nálezy všech pravidel (nejzávažnější první) s počty podle závažnosti — souhrn nad katalogem. */
export function useNalezySouhrn(filtr: FiltrNalezu, volby: { enabled?: boolean } = {}) {
  const parametry = new URLSearchParams({ velikost: '20' });
  if (filtr.zavaznost) parametry.set('zavaznost', filtr.zavaznost);
  if (filtr.oblast) parametry.set('oblast', filtr.oblast);
  return useQuery({
    queryKey: [...KLIC_KONTROLY, 'souhrn', filtr.zavaznost ?? '', filtr.oblast ?? ''] as const,
    queryFn: ({ signal }) =>
      api.get<SignalSeznam>(`/kontroly-souladu/nalezy?${parametry.toString()}`, { signal }),
    placeholderData: keepPreviousData,
    enabled: volby.enabled ?? true,
  });
}

export const nastavitPravidlo = (kod: string, zapnuto: boolean) =>
  api.put<{ kod: string; zapnuto: boolean }>(
    `/kontroly-souladu/pravidla/${encodeURIComponent(kod)}`,
    { zapnuto },
  );

export const prepocitatKontroly = () =>
  api.post<PrepocetKontrol>('/kontroly-souladu/prepocitat', {});

/** „Ignorovat s důvodem“ u nálezu: vrací uzavřený signál i založenou výjimku (její id slouží ke Zpět). */
export const ignorovatNalez = (signalId: string, duvod: string) =>
  api.post<IgnorovanoSouladu>(`/signaly/${encodeURIComponent(signalId)}/ignorovat`, {
    duvod: duvod.trim(),
  });

export const odebratVyjimku = (id: string) =>
  api.delete<{ id: string }>(`/kontroly-souladu/vyjimky/${encodeURIComponent(id)}`);

export const vratitVyjimku = (id: string) =>
  api.post<VyjimkaSouladu>(`/kontroly-souladu/vyjimky/${encodeURIComponent(id)}/obnovit`, {});

/** Po zápisu, který mění výsledek kontrol, se načte znovu katalog, výjimky i signály na Domů. */
export function obnovitKontroly(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: KLIC_KONTROLY });
}
